import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import Modal from 'components/Shared/Modal'
import Button from 'components/Shared/Button'
import { deleteUser, unsubscribeUserFromMarketing } from 'ducks/admin/users'
import { toast } from 'react-toastify'

const UserDeletionModal = ({ user, isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch()
  const [deletionType, setDeletionType] = useState('marketing') // 'marketing' or 'full'
  const [confirmationStep, setConfirmationStep] = useState(1) // 1: choose type, 2: confirm
  const [confirmationText, setConfirmationText] = useState('')
  const [loading, setLoading] = useState(false)

  if (!isOpen || !user) {
    return null
  }

  const activeOrganizations =
    user.Organizations?.filter(org => org.active) || []
  const hasActiveSubscriptions = activeOrganizations.length > 0

  const handleClose = () => {
    setConfirmationStep(1)
    setDeletionType('marketing')
    setConfirmationText('')
    setLoading(false)
    onClose()
  }

  const handleNext = () => {
    if (confirmationStep === 1) {
      setConfirmationStep(2)
    }
  }

  const handleBack = () => {
    if (confirmationStep === 2) {
      setConfirmationStep(1)
      setConfirmationText('')
    }
  }

  const handleConfirmDeletion = async () => {
    if (deletionType === 'full' && confirmationText !== 'DELETE') {
      toast.error('Please type DELETE to confirm')

      return
    }

    if (deletionType === 'marketing' && confirmationText !== 'UNSUBSCRIBE') {
      toast.error('Please type UNSUBSCRIBE to confirm')

      return
    }

    setLoading(true)

    try {
      if (deletionType === 'full') {
        await dispatch(deleteUser(user.id))
        toast.success('User successfully deleted')
      } else {
        await dispatch(unsubscribeUserFromMarketing(user.id))
        toast.success('User successfully unsubscribed from marketing')
      }

      if (onSuccess) {
        onSuccess()
      }

      handleClose()
    } catch (error) {
      console.error('Deletion error:', error)
      toast.error(error.message || 'Failed to delete user')
    } finally {
      setLoading(false)
    }
  }

  const renderStepOne = () => (
    <>
      <Modal.Header
        title={`Manage User: ${user.name}`}
        color="red"
        goBack={handleClose}
      />
      <Modal.Content>
        <div className="user-deletion-options">
          <h3>Choose an action for this user:</h3>

          <div className="deletion-option">
            <label>
              <input
                type="radio"
                name="deletionType"
                value="marketing"
                checked={deletionType === 'marketing'}
                onChange={e => setDeletionType(e.target.value)}
              />
              <strong>Unsubscribe from Marketing Only</strong>
            </label>
            <p className="option-description">
              Remove user from marketing lists and suppress future marketing
              emails. User account and apps remain active.
            </p>
          </div>

          <div className="deletion-option">
            <label>
              <input
                type="radio"
                name="deletionType"
                value="full"
                checked={deletionType === 'full'}
                onChange={e => setDeletionType(e.target.value)}
              />
              <strong>Full Account Deletion</strong>
            </label>
            <p className="option-description">
              Permanently delete user account with data anonymization. This
              action cannot be undone. User must not have active apps or
              subscriptions.
            </p>
          </div>

          <div className="user-info">
            <h4>User Details:</h4>
            <p>
              <strong>Name:</strong> {user.name}
            </p>
            <p>
              <strong>Email:</strong> {user.email}
            </p>
            <p>
              <strong>Organizations:</strong>{' '}
              {user.Organizations?.map(org => org.name).join(', ') || 'None'}
            </p>
            {user.Apps && user.Apps.length > 0 && (
              <div className="active-apps-warning">
                <p style={{ color: 'red' }}>
                  <strong>
                    <span role="img" aria-label="warning">
                      ⚠️
                    </span>{' '}
                    Warning:
                  </strong>{' '}
                  User has {user.Apps.length} active app(s). Full deletion is
                  not possible until apps are deleted.
                </p>
                <ul>
                  {user.Apps.slice(0, 5).map(app => (
                    <li key={app.id}>{app.name}</li>
                  ))}
                  {user.Apps.length > 5 && (
                    <li>... and {user.Apps.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            {hasActiveSubscriptions && (
              <div className="active-apps-warning">
                <p style={{ color: 'red' }}>
                  <strong>
                    <span role="img" aria-label="warning">
                      ⚠️
                    </span>{' '}
                    Warning:
                  </strong>{' '}
                  User has {activeOrganizations.length} active subscription(s).
                  Full deletion is not possible until subscriptions are
                  canceled.
                </p>
                <ul>
                  {activeOrganizations.map(org => (
                    <li key={org.id}>{org.name} (Active Subscription)</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} variant="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleNext}
          color="red"
          disabled={
            deletionType === 'full' &&
            ((user.Apps && user.Apps.length > 0) || hasActiveSubscriptions)
          }
        >
          Continue
        </Button>
      </Modal.Actions>
    </>
  )

  const renderStepTwo = () => (
    <>
      <Modal.Header title="Confirm Action" color="red" goBack={handleBack} />
      <Modal.Content>
        <div className="confirmation-step">
          {deletionType === 'full' ? (
            <>
              <h3>
                <span role="img" aria-label="warning">
                  ⚠️
                </span>{' '}
                Permanent User Deletion
              </h3>
              <p>
                You are about to permanently delete <strong>{user.name}</strong>{' '}
                ({user.email}). This action will:
              </p>
              <ul>
                <li>Anonymize personal data (GDPR compliance)</li>
                <li>Unsubscribe from all marketing communications</li>
                <li>Create an audit log for compliance</li>
              </ul>
              <p>
                <strong>This action cannot be undone.</strong>
              </p>

              <div className="confirmation-input">
                <label>
                  Type <strong>DELETE</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={e => setConfirmationText(e.target.value)}
                  placeholder="DELETE"
                  autoFocus
                />
              </div>
            </>
          ) : (
            <>
              <h3>Marketing Unsubscribe</h3>
              <p>
                You are about to unsubscribe <strong>{user.name}</strong> (
                {user.email}) from marketing. This action will:
              </p>
              <ul>
                <li>Remove user from all marketing lists</li>
                <li>Add email to suppression list</li>
                <li>Send confirmation email to user</li>
                <li>Create an audit log</li>
              </ul>
              <p>User account and apps will remain active.</p>

              <div className="confirmation-input">
                <label>
                  Type <strong>UNSUBSCRIBE</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={e => setConfirmationText(e.target.value)}
                  placeholder="UNSUBSCRIBE"
                  autoFocus
                />
              </div>
            </>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleBack} variant="secondary">
          Back
        </Button>
        <Button
          onClick={handleConfirmDeletion}
          color="red"
          loading={loading}
          disabled={
            loading ||
            (deletionType === 'full' && confirmationText !== 'DELETE') ||
            (deletionType === 'marketing' && confirmationText !== 'UNSUBSCRIBE')
          }
        >
          {loading
            ? 'Processing...'
            : deletionType === 'full'
            ? 'Delete User'
            : 'Unsubscribe User'}
        </Button>
      </Modal.Actions>
    </>
  )

  return (
    <Modal size="md" onClose={handleClose}>
      {confirmationStep === 1 ? renderStepOne() : renderStepTwo()}
    </Modal>
  )
}

export default UserDeletionModal
